import { React, useRef } from '../../shared/react.js';
import { Icon } from '../../shared/icons.js';
import { ArtworkImage } from '../../shared/ui/ArtworkImage.js';
import { useAppRuntime, useAppState } from '../../app/app.context.js';
import {
  compareArtworks,
  queryArtwork,
  selectedArtwork,
  selectedHit,
  selectedMatchEvidence,
  selectedSimilarity
} from '../../app/app.selectors.js';
import { ComparePanel } from '../compare/ComparePanel.js';

function artworkSummary(creator: string, objectDate: string, year: number | null): string {
  const maker = creator || 'Unknown maker';
  const date = objectDate || (year === null ? 'Date unknown' : String(year));
  return `${maker} · ${date}`;
}

export function InspectorPanel() {
  const runtime = useAppRuntime();
  const compareTrigger = useRef(null as HTMLButtonElement | null);
  const state = useAppState();
  const selected = selectedArtwork(state);
  const query = queryArtwork(state);
  const artwork = selected ?? query;
  const evidence = selectedMatchEvidence(state);
  const isCurrentMatch = Boolean(selectedHit(state));
  const similarity = selectedSimilarity(state);
  const comparing = compareArtworks(state).length >= 2;

  if (!artwork) {
    return (
      <aside className="inspector-panel inspector-panel--empty" aria-label="Search inspector">
        <div className="inspector-panel__header"><span>Inspector</span></div>
        <p>Select an artwork to refine the search.</p>
      </aside>
    );
  }

  const inCollections = state.savedCollections.filter((collection) => collection.artworkIds.includes(artwork.id));

  return (
    <aside className="inspector-panel" aria-label="Search inspector">
      <div className="inspector-panel__header">
        <span>Inspector</span>
        <a href={artwork.objectUrl} target="_blank" rel="noreferrer" aria-label="Open the Met source record"><Icon name="external" size={14}/></a>
      </div>

      <div className="inspector-preview inspector-preview--hero">
        <ArtworkImage
          src={artwork.detailImageUrl ?? artwork.imageUrl ?? artwork.thumbnailUrl}
          alt={artwork.title}
          role="detail" sizes="(max-width: 900px) 90vw, 280px" fallbackSrc={artwork.thumbnailUrl}
          fallbackLabel="Artwork"
          width={artwork.imageWidth}
          height={artwork.imageHeight}
          dominantColor={artwork.dominantColor}
        />
      </div>

      <div className="inspector-copy inspector-copy--artwork">
        <h2>{artwork.title}</h2>
        <p>{artworkSummary(artwork.creator, artwork.objectDate, artwork.year)}</p>
        <span>{artwork.department || 'The Metropolitan Museum of Art'}</span>
      </div>

      <div className="inspector-action-list" aria-label="Refine from this artwork">
        {runtime.experimentalVisual && <><button type="button" onClick={() => void runtime.moreLike(artwork.id)}><Icon name="spark" size={16}/><span>More like this</span></button>
        <button type="button" onClick={() => void runtime.lessLike(artwork.id)}><Icon name="circle-minus" size={16}/><span>Less like this</span></button>
        <button type="button" onClick={() => void runtime.pinToQuery(artwork.id)}><Icon name="pin" size={16}/><span>Pin to query</span></button></>}
        <button ref={compareTrigger} type="button" onClick={() => runtime.compareArtwork(artwork.id)} className={comparing ? 'is-active' : ''}><Icon name="compare" size={16}/><span>Compare</span></button>
      </div>

      <ComparePanel onClose={() => compareTrigger.current?.focus({ preventScroll: true })}/>

      {runtime.experimentalVisual && <section className="inspector-detail-search" aria-labelledby="detail-search-title">
        <div className="inspector-section-title"><h3 id="detail-search-title">Search this detail</h3></div>
        <button type="button" onClick={() => runtime.openDetailSearch(artwork.id)}>
          <span className="inspector-detail-search__thumb"><ArtworkImage src={artwork.thumbnailUrl ?? artwork.imageUrl} alt="" width={artwork.imageWidth} height={artwork.imageHeight} dominantColor={artwork.dominantColor}/></span>
          <span><strong>Search visually similar</strong><small>Use a selected area from this artwork.</small></span>
          <Icon name="arrow-right" size={16}/>
        </button>
      </section>}

      <section className="inspector-why" aria-labelledby="why-matched-title">
        <div className="inspector-section-title">
          <h3 id="why-matched-title">{isCurrentMatch ? 'Why this matched' : 'Artwork context'}</h3>
          {similarity !== null && <span>{similarity.toFixed(3)}</span>}
        </div>
        {isCurrentMatch ? <ul>
          {evidence.length > 0 ? evidence.map((reason) => <li key={reason.id}><Icon name="check" size={12}/><span>{reason.label}</span></li>) : <li><Icon name="check" size={12}/><span>Closest in the composed vector space</span></li>}
        </ul> : <p>This artwork is outside the current results.</p>}
      </section>

      <details className="inspector-details">
        <summary>Artwork details</summary>
        <dl>
          <div><dt>Maker</dt><dd>{artwork.creator || 'Unknown maker'}</dd></div>
          <div><dt>Date</dt><dd>{artwork.objectDate || artwork.year || 'Unknown'}</dd></div>
          <div><dt>Medium</dt><dd>{artwork.medium || artwork.classification || 'Not recorded'}</dd></div>
          <div><dt>Culture</dt><dd>{artwork.culture || artwork.period || 'Not recorded'}</dd></div>
        </dl>
      </details>

      <section className="inspector-collections" aria-labelledby="inspector-collections-title">
        <div className="inspector-section-title"><h3 id="inspector-collections-title">Collections</h3></div>
        <div className="inspector-collections__list">
          {state.savedCollections.slice(0, 3).map((collection) => {
            const contained = collection.artworkIds.includes(artwork.id);
            return (
              <button type="button" key={collection.id} onClick={() => void runtime.addArtworkToCollection(collection.id, artwork.id)} className={contained ? 'is-contained' : ''}>
                <span className="saved-collection__swatch" style={{ backgroundColor: collection.color }}/><span>{collection.name}</span>{contained ? <Icon name="check" size={13}/> : <Icon name="plus" size={13}/>} 
              </button>
            );
          })}
          {state.savedCollections.length === 0 && (
            <button type="button" onClick={() => void runtime.createCollectionFromArtwork(artwork.id)}><Icon name="folder" size={14}/><span>Create a collection</span><Icon name="plus" size={13}/></button>
          )}
        </div>
        {inCollections.length > 0 && <p>{inCollections.length === 1 ? 'Saved in 1 collection' : `Saved in ${inCollections.length} collections`}</p>}
      </section>
    </aside>
  );
}
